/**
 * iter160 — serialize a Tiptap ProseMirror document into the mini-Markdown
 * string that iter156/159 stores in `TeamMsgReq.body`. Keeping the same
 * on-the-wire format means:
 *
 *   • Backend, DB schema, `renderInlineFormat`, and message-bubble code
 *     are all untouched.
 *   • Existing plain-text and mini-Markdown messages keep rendering
 *     byte-identical (the renderer is a superset of plain text).
 *   • Attachments, forward, preview, and permissions all still work.
 *
 * Format contract (round-trip):
 *   bold          → **text**
 *   italic        → _text_
 *   underline     → __text__      (new in iter160; renderer parses it)
 *   link          → [label](url)
 *   hard-break    → \n
 *   paragraph gap → \n\n
 *   bullet list   → "- item"   per <li>
 *   ordered list  → "1. item"  per <li>, auto-numbered
 *
 * We walk the ProseMirror JSON — NOT the HTML — because the JSON is the
 * canonical semantic representation and can't contain unsafe attributes.
 */

function escapeInline(text) {
  if (!text) return "";
  // Escape our own inline markers so a literal `*` or `_` never re-parses
  // as formatting on the recipient side.
  return String(text).replace(/([*_])/g, "\\$1");
}

function applyMarks(text, marks = []) {
  let out = escapeInline(text);
  if (!marks.length) return out;
  // Order matters: apply outer marks last so nesting round-trips.
  //   underline > bold > italic > link (inside-out)
  const has = (name) => marks.some(m => m.type === name);
  const link = marks.find(m => m.type === "link");
  if (has("italic"))    out = `_${out}_`;
  if (has("bold"))      out = `**${out}**`;
  if (has("underline")) out = `__${out}__`;
  if (link) {
    const href = (link.attrs && link.attrs.href) || "";
    if (/^(https?:|mailto:)/i.test(href) && !out.includes("]") && !out.includes("\n")) {
      out = `[${out}](${href})`;
    }
  }
  return out;
}

function nodeToText(node, ctx = { orderedCounter: null }) {
  if (!node) return "";
  switch (node.type) {
    case "doc":
    case "listItem":
      return (node.content || []).map(n => nodeToText(n, ctx)).join("");
    case "paragraph": {
      const inner = (node.content || []).map(n => nodeToText(n, ctx)).join("");
      // Paragraphs stack with a single \n; blank paragraphs become the blank
      // line separator the recipient needs to see.
      return inner + "\n";
    }
    case "hardBreak":
      return "\n";
    case "text":
      return applyMarks(node.text || "", node.marks || []);
    case "bulletList": {
      const items = (node.content || []).map((li) => {
        const inner = (li.content || []).map(n => nodeToText(n, ctx)).join("");
        return "- " + inner.replace(/\n+$/, "");
      });
      return items.join("\n") + "\n";
    }
    case "orderedList": {
      let i = (node.attrs && node.attrs.start) || 1;
      const items = (node.content || []).map((li) => {
        const inner = (li.content || []).map(n => nodeToText(n, ctx)).join("");
        return `${i++}. ` + inner.replace(/\n+$/, "");
      });
      return items.join("\n") + "\n";
    }
    case "heading": {
      const inner = (node.content || []).map(n => nodeToText(n, ctx)).join("");
      // Headings collapse to a bold paragraph — matches how iter156 renders
      // them (no dedicated heading style in the bubble renderer).
      return `**${inner.replace(/\n+$/, "")}**\n`;
    }
    default:
      return (node.content || []).map(n => nodeToText(n, ctx)).join("");
  }
}

/**
 * Convert Tiptap's editor.getJSON() output to the mini-Markdown string
 * stored in Mongo. Trims trailing whitespace and collapses runs of blank
 * lines to at most one (matches the sanitizer's rule).
 */
export function docToMiniMarkdown(doc) {
  if (!doc) return "";
  let out = nodeToText(doc);
  return out
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\s+|\s+$/g, "");
}

/**
 * Count visible characters — the same characters the recipient sees.
 * Markdown markers, list numbers, and link URLs are NOT counted, which
 * is why we walk the ProseMirror text nodes directly and only count
 * their raw string content (post-escape but ignoring the escape chars).
 * Newlines DO count (they're visible whitespace) — matches how iter159's
 * plain-string counter behaved.
 */
export function visibleTextLength(doc) {
  if (!doc) return 0;
  let n = 0;
  const walk = (node) => {
    if (!node) return;
    if (node.type === "text") { n += (node.text || "").length; return; }
    if (node.type === "hardBreak") { n += 1; return; }
    if (Array.isArray(node.content)) {
      for (const c of node.content) walk(c);
      // Paragraph / list-item boundaries add one visible newline each
      // (except the very last one, which the recipient trims).
      if (node.type === "paragraph" || node.type === "listItem") n += 1;
    }
  };
  walk(doc);
  // Subtract 1 for the trailing paragraph's newline that the serializer
  // trims off — so 4000 typed chars round-trips as 4000.
  return Math.max(0, n - 1);
}
